import { ILesson, ISection, TIME_HOURS_LS, TIME_MINUTE_LS } from "../type";

type PrefetchPayload = {
  type: "WW_PREFETCH_URLS";
  urls: string[];
};

function toAbsoluteUrl(path: string): string | null {
  const raw = String(path || "").trim();
  if (!raw) return null;
  try {
    return new URL(raw, window.location.origin).toString();
  } catch {
    return null;
  }
}

function lessonStartIndex(section: ISection, round: number): number {
  const shed = section.UserLessonsScheduled.find((s) => Number(s.round) === Number(round));
  if (!shed) return 0;

  const today = new Date();
  const targetDate = new Date(shed.start_date);
  const h = today.getTimezoneOffset() / 60;
  targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
  const diff = Math.floor(
    Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
  );
  return Math.max(0, diff);
}

function getCurrentAndNextLessons(sections: ISection[]): ILesson[] {
  const allSchedules = sections
    .flatMap((s) => s.UserLessonsScheduled || [])
    .sort((a, b) => +new Date(a.start_date) - +new Date(b.start_date));
  if (allSchedules.length === 0) return [];

  const now = new Date();
  let current = allSchedules[allSchedules.length - 1];
  for (let i = 0; i < allSchedules.length; i++) {
    const d = new Date(allSchedules[i].start_date);
    const h = now.getTimezoneOffset() / 60;
    d.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    if (+d > +now) {
      current = allSchedules[Math.max(0, i - 1)];
      break;
    }
  }

  const section = sections.find((s) => s.id === current.SectionId);
  if (!section) return [];
  const roundLessons = (section.Lesson_SectionRelation || [])
    .filter((l) => Number(l.round) === Number(current.round))
    .sort((a, b) => a.sequence_number__lesson - b.sequence_number__lesson);
  if (roundLessons.length === 0) return [];

  const start = lessonStartIndex(section, current.round);
  return roundLessons.slice(start, start + 4);
}

function buildPrefetchUrls(lessons: ILesson[]): string[] {
  const urls = new Set<string>();
  for (const lesson of lessons) {
    urls.add(`/section/${lesson.section_id}/lesson/${lesson.id}`);
    for (const ex of lesson.Exercise_LessonRelation || []) {
      const mediaCandidates = [ex.url, ex.previwUrl];
      for (const m of mediaCandidates) {
        const abs = toAbsoluteUrl(m);
        if (!abs) continue;
        if (abs.startsWith(window.location.origin)) {
          urls.add(abs.replace(window.location.origin, ""));
        } else {
          urls.add(abs);
        }
      }
    }
  }
  return Array.from(urls);
}

async function dispatchPrefetch(urls: string[]): Promise<string[]> {
  if (urls.length === 0) return [];

  // 1) Primary way: ask SW to warm cache in background.
  if ("serviceWorker" in navigator) {
    const payload: PrefetchPayload = { type: "WW_PREFETCH_URLS", urls };
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage(payload);
    } else {
      // В первой сессии после установки PWA controller может быть еще не привязан.
      // Пробуем отправить в active/waiting worker, чтобы prefetch не пропал.
      try {
        const reg = await navigator.serviceWorker.ready;
        reg.active?.postMessage(payload);
        reg.waiting?.postMessage(payload);
        reg.installing?.postMessage(payload);
      } catch {
        // ignore
      }
    }
  }

  // 2) Fallback: fire-and-forget network warmup (will be cached by SW fetch handler).
  void Promise.all(
    urls.map((u) =>
      fetch(u, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      }).catch(() => undefined)
    )
  );

  return urls;
}

function getFromCurrentLessonAndNext3(
  sections: ISection[],
  sectionId: number,
  lessonId: number
): ILesson[] {
  const section = sections.find((s) => Number(s.id) === Number(sectionId));
  if (!section) return [];
  const all = (section.Lesson_SectionRelation || []).slice();
  const current = all.find((l) => Number(l.id) === Number(lessonId));
  if (!current) return [];
  const round = Number(current.round);
  const roundLessons = all
    .filter((l) => Number(l.round) === round)
    .sort((a, b) => Number(a.sequence_number__lesson) - Number(b.sequence_number__lesson));
  const idx = roundLessons.findIndex((l) => Number(l.id) === Number(lessonId));
  if (idx < 0) return [];
  return roundLessons.slice(idx, idx + 4);
}

export async function prefetchCurrentAndNextLessons(sections: ISection[]): Promise<string[]> {
  if (typeof window === "undefined" || sections.length === 0) return [];
  const lessons = getCurrentAndNextLessons(sections);
  const urls = buildPrefetchUrls(lessons);
  return dispatchPrefetch(urls);
}

export async function prefetchFromCurrentLesson(
  sections: ISection[],
  sectionId: number,
  lessonId: number
): Promise<string[]> {
  if (typeof window === "undefined" || sections.length === 0) return [];
  const lessons = getFromCurrentLessonAndNext3(sections, sectionId, lessonId);
  const urls = buildPrefetchUrls(lessons);
  return dispatchPrefetch(urls);
}

